// third party imports
import { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// internal rebet imports
import "./stylesUserManager.css";
import {
  ADMIN_TYPES,
  USER_HEADER_TABS,
} from "../../../utils/Constants";
import { fetchSpecificUsersData } from "../../../api/CommonFunctions/CommonFunctions";
import createPrivateAdminPanelInstance from "../../../api/PrivateAdminPanelInstance";

// component imports
import UserManagerHeader from "./UserManagerHeader/UserManagerHeader";
import BetTable from "./BetsTable/BetTable";
import TransactionTable from "./TransactionsTable/TransactionTable";
import PaymentsTable from "./PaymentsTable/PaymentsTable";
import FeatureFlagGuard from "../../FeatureFlagGuard/FeatureFlagGuard";
import FeatureFlagService from "../../../services/FeatureFlagService";
import { TRIVELTA_PRODUCTS } from "../../../utils/products";

// asset imports
import CasinoBetsTable from "./CasinoBetsTable/CasinoBetsTable";
import Poker from "./Poker/Poker";
import { useGetAdmins } from "../../../queries/useGetAdmins";
import GamePass from "./GamePass/GamePass";
import PersonalInfo from "./PersonalInfo/PersonalInfo";
import UserCard from "./UserCard/UserCard";
import PikkemBets from "./PikkemBets/PikkemBets";
import UserWalletsGraph from "./UserWalletsGraph/UserWalletsGraph";
import UserPromotions from "./Promotions/UserPromotions";
import useIsDevMode from "../../../hooks/useIsDevMode";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";
import { AppStrings } from "../../../utils/constants/AppStrings";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Product gate per deep-linkable subtab — mirrors the (product, fallback)
// pairs on the FeatureFlagGuard wrappers below, so a `?tab=` URL can't select
// a tab whose panel the guard would blank out.
// TODO(PAMENG-1820): drop the fallback features with the guard cleanup.
const TAB_PRODUCT_REQUIREMENTS = {
  [USER_HEADER_TABS.BETS]: [TRIVELTA_PRODUCTS.SPORTSBOOK, "sportsbook_v2"],
  [USER_HEADER_TABS.CASINO_BETS]: [TRIVELTA_PRODUCTS.CASINO, "casino"],
  [USER_HEADER_TABS.POKER_BETS]: [TRIVELTA_PRODUCTS.POKER, "poker"],
  [USER_HEADER_TABS.PIKKEM]: [TRIVELTA_PRODUCTS.PIKKEM, "pikkem"],
  [USER_HEADER_TABS.GAME_PASS]: [TRIVELTA_PRODUCTS.GAME_PASS, "gamePass"],
};

const UserManager = () => {
  // non-state variables
  const navigate = useNavigate();
  const location = useLocation();
  const isDevMode = useIsDevMode();
  const currencySymbol = useCurrencySymbol();

  const query = useQuery();
  const tabFromUrl = query.get("tab");
  const userIdFromUrl = query.get("userId");
  const openModal = query.get("open");

  const { state } = useLocation();
  const token = JSON.parse(localStorage.getItem("token"));
  const email = JSON.parse(localStorage.getItem("email"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  // state variables

  const [userData, setUserData] = useState(null);
  const [walletActive, setWalletActive] = useState(false);
  const [selectedTab, setSelectedTab] = useState(USER_HEADER_TABS?.OVERVIEW);
  const [isLoading, setIsLoading] = useState(true);
  const [upToDateUserData, setUpToDateUserData] = useState({});
  const { data: adminsData = [] } = useGetAdmins();

  usePageTitle(upToDateUserData?.username || AppStrings.APP_USERS);

  // use effects
  useEffect(() => {
    if (userData) {
      setUpToDateUserData(userData);
    }
  }, [userData]);

  useEffect(() => {
    if (
      !tabFromUrl ||
      !Object.values(USER_HEADER_TABS).includes(tabFromUrl.toLowerCase())
    ) {
      return undefined;
    }
    const tab = tabFromUrl.toLowerCase();
    if (
      (tab === USER_HEADER_TABS.PROMOTIONS ||
        tab === USER_HEADER_TABS.PAYMENTS) &&
      !isDevMode
    ) {
      return undefined;
    }
    const requirement = TAB_PRODUCT_REQUIREMENTS[tab];
    if (!requirement) {
      setSelectedTab(tab);
      return undefined;
    }
    // Product-gated tab: honour the deep link only when the product is
    // active; otherwise stay on the OVERVIEW default. Fail closed on config
    // errors, matching FeatureFlagGuard.
    let cancelled = false;
    const applyIfProductActive = async () => {
      try {
        await FeatureFlagService.initialize();
        const [product, fallbackFeature] = requirement;
        if (
          !cancelled &&
          FeatureFlagService.isProductActive(product, fallbackFeature)
        ) {
          setSelectedTab(tab);
        }
      } catch (error) {
        console.error(error);
      }
    };
    applyIfProductActive();
    return () => {
      cancelled = true;
    };
  }, [isDevMode, tabFromUrl]);

  useEffect(() => {
    const loadUserData = async () => {
      // 1. Priority: data from state (internal navigation)
      if (state?.user_data) {
        setUserData(state.user_data);
        setWalletActive(state.wallet_active);
        return;
      }

      // 2. Fallback: localStorage (for window.open from other tabs)
      const storedUser = localStorage.getItem("selected_user_data");
      const storedWalletActive = localStorage.getItem("wallet_active");

      if (storedUser) {
        setUserData(JSON.parse(storedUser));
        setWalletActive(storedWalletActive === "true");
        // Remove immediately to avoid contamination between tabs
        localStorage.removeItem("selected_user_data");
        localStorage.removeItem("wallet_active");
        return;
      }

      // 3. Fallback final: search via API using userId from URL (for refresh)
      if (userIdFromUrl) {
        const fetchedData = await fetchSpecificUsersData(
          PrivateAdminPanelInstance,
          userIdFromUrl,
        );
        const userData = fetchedData?.length ? fetchedData[0] : fetchedData;
        if (userData) {
          setUserData(userData);
          setWalletActive(false);
        }
      }
    };

    loadUserData();
  }, [state, userIdFromUrl]);

  // api functions
  const refreshUserData = async (optimisticOverride = {}) => {
    const userId = upToDateUserData?.user_id || state?.user_data?.user_id;
    if (!userId) {
      return;
    }

    const newest_data_fetched = await fetchSpecificUsersData(
      PrivateAdminPanelInstance,
      userId,
    );

    const newest_data = newest_data_fetched?.length
      ? newest_data_fetched[0]
      : newest_data_fetched;

    const mergedData = {
      ...upToDateUserData,
      ...(newest_data || {}),
      ...optimisticOverride,
    };

    setUpToDateUserData(mergedData);

    // Update the location state to keep the same URL
    navigate(location.pathname + location.search, {
      state: {
        user_data: mergedData,
        wallet_active: mergedData?.wallet_active,
      },
      replace: true,
    });
  };

  // helper functions
  const handleBackPressed = () => {
    navigate(-1);
  };

  const handleSetSelectedTab = (tab) => {
    if (
      (tab === USER_HEADER_TABS.PROMOTIONS ||
        tab === USER_HEADER_TABS.PAYMENTS) &&
      !isDevMode
    ) {
      return;
    }
    setSelectedTab(tab);
  };

  const adminData = useMemo(() => {
    if (!adminsData?.length) {
      return;
    }

    return adminsData.find(
      (admin) => admin.email === email || admin.username === email,
    );
  }, [adminsData, email]);

  const isSuperAdmin = adminData?.group_name === ADMIN_TYPES.SUPER_ADMIN;

  return (
    <>
      <div className="panel-container">
        <UserManagerHeader
          selectedTab={selectedTab}
          setSelectedTab={handleSetSelectedTab}
        />

        <div className="body-container">
          {selectedTab === USER_HEADER_TABS?.OVERVIEW ? (
            <>
              <div className="user-manager-card-container">
                <UserCard
                  data={upToDateUserData}
                  refreshUserData={refreshUserData}
                  setIsLoading={setIsLoading}
                  isLoading={isLoading}
                  openModal={openModal}
                  currencySymbol={currencySymbol}
                />
                <div className="user-manager-personal-user-info-container">
                  <UserWalletsGraph userData={upToDateUserData} />
                  <PersonalInfo data={upToDateUserData} isLoading={isLoading} />
                </div>
              </div>
            </>
          ) : selectedTab === USER_HEADER_TABS?.BETS ? (
            // TODO(PAMENG-1820): drop the `feature` fallback on the 5 guards
            // in this ternary after rollout.
            <FeatureFlagGuard
              mode="conditional"
              product={TRIVELTA_PRODUCTS.SPORTSBOOK}
              feature="sportsbook_v2"
            >
              <BetTable
                userData={upToDateUserData}
                adminData={adminData}
                currencySymbol={currencySymbol}
              />
            </FeatureFlagGuard>
          ) : selectedTab === USER_HEADER_TABS?.CASINO_BETS ? (
            <FeatureFlagGuard
              product={TRIVELTA_PRODUCTS.CASINO}
              mode="conditional"
              feature="casino"
            >
              <CasinoBetsTable
                userData={upToDateUserData}
                adminData={adminData}
                currencySymbol={currencySymbol}
              />
            </FeatureFlagGuard>
          ) : selectedTab === USER_HEADER_TABS?.POKER_BETS ? (
            <FeatureFlagGuard
              mode="conditional"
              product={TRIVELTA_PRODUCTS.POKER}
              feature="poker"
            >
              <Poker
                userData={upToDateUserData}
                currencySymbol={currencySymbol}
              />
            </FeatureFlagGuard>
          ) : selectedTab === USER_HEADER_TABS?.PIKKEM ? (
            <FeatureFlagGuard
              mode="conditional"
              product={TRIVELTA_PRODUCTS.PIKKEM}
              feature="pikkem"
            >
              <PikkemBets
                userData={upToDateUserData}
                adminData={adminData}
                currencySymbol={currencySymbol}
              />
            </FeatureFlagGuard>
          ) : selectedTab === USER_HEADER_TABS?.GAME_PASS ? (
            <FeatureFlagGuard
              mode="conditional"
              product={TRIVELTA_PRODUCTS.GAME_PASS}
              feature="gamePass"
            >
              <GamePass userData={upToDateUserData} />
            </FeatureFlagGuard>
          ) : selectedTab === USER_HEADER_TABS?.PROMOTIONS ? (
            <div
              className="user-manager-promotions-container"
              data-testid="promotions-tab-content"
            >
              <UserPromotions
                userId={upToDateUserData?.user_id}
                currencySymbol={currencySymbol}
              />
            </div>
          ) : selectedTab === USER_HEADER_TABS?.TRANSACTIONS ? (
            <div className="wallet-container">
              <TransactionTable
                userData={upToDateUserData}
                adminData={adminData}
                currencySymbol={currencySymbol}
              />
            </div>
          ) : selectedTab === USER_HEADER_TABS?.PAYMENTS ? (
            <div className="wallet-container" data-testid="payments-tab-content">
              <PaymentsTable
                userData={upToDateUserData}
                adminData={adminData}
                currencySymbol={currencySymbol}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default UserManager;
